import {zodResolver} from "@hookform/resolvers/zod";
import {useOnboardingStatus, useUpdateFeature} from "@mytour/platform";
import type {ICompany, UpdateFeaturePayload} from "@mytour/shared";
import {NoButton, YesButton} from "@mytour/ui/form";
import {SplitterButtons} from "@mytour/ui/layout";
import {useState} from "react";
import {useForm} from "react-hook-form";
import {z} from "zod";
import {ModalContent, ModalContentWrapper} from "../../ModalContent";
import {Step2} from "./components/Step2";
import {Step3} from "./components/Step3";

type AStep = 0 | 1;

type Props = {company: ICompany};

const schema = z.object({
  notifDayBefore: z.boolean(),
  notifAtSave: z.boolean(),
});
type Form = z.infer<typeof schema>;

export function AccountFeatures({company}: Props) {
  const [pending, setPending] = useState(false);
  const [step, setStep] = useState<AStep>(0);
  const {setOnboardingStatus} = useOnboardingStatus();
  const {updateFeature} = useUpdateFeature();

  const {notificationAtEventSave, notificationRemind} = company.features;

  const form = useForm<Form>({
    resolver: zodResolver(schema),
    defaultValues: {notifAtSave: notificationAtEventSave, notifDayBefore: notificationRemind},
  });

  const firstStep = 0;
  const lastStep = 1;

  return (
    <ModalContentWrapper>
      <ModalContent>
        {step === firstStep && <Step2 />}
        {step === lastStep && <Step3 />}
      </ModalContent>
      {step === 0 && (
        <SplitterButtons>
          <NoButton
            onClick={() => {
              form.setValue("notifDayBefore", false);
              handleToNextStep();
            }}
            disabled={pending}
            pending={pending}
          />
          <YesButton
            onClick={() => {
              form.setValue("notifDayBefore", true);
              handleToNextStep();
            }}
            disabled={pending}
            pending={pending}
          />
        </SplitterButtons>
      )}
      {step === 1 && (
        <SplitterButtons>
          <NoButton
            onClick={() => {
              form.setValue("notifAtSave", false);
              handleSubmit();
            }}
            disabled={pending}
            pending={pending}
          />
          <YesButton
            onClick={() => {
              form.setValue("notifAtSave", true);
              handleSubmit();
            }}
            disabled={pending}
            pending={pending}
          />
        </SplitterButtons>
      )}
    </ModalContentWrapper>
  );

  function handleToNextStep() {
    setPending(true);
    try {
      const nextStep = step + 1;
      if (nextStep <= lastStep) {
        setStep(nextStep as AStep);
      }
    } finally {
      setPending(false);
    }
  }

  async function handleSubmit() {
    setPending(true);
    try {
      const {notifAtSave, notifDayBefore} = form.getValues();
      const entity: UpdateFeaturePayload = {
        notificationAtEventSave: notifAtSave,
        notificationRemind: notifDayBefore,
      };
      await updateFeature(entity);
      handleHelp();
    } finally {
      setPending(false);
    }
  }

  function handleHelp() {
    setOnboardingStatus("HELP");
  }
}
